// evo/rooms/EvoKidsStayFreeBadge.tsx
// Green "success" pill shown at the top of a room card (right below the room name) when any of the
// room's rates carries a CHILDREN_FREE_OFFER promotion. Mirrors EvoRoomAvailabilityAlertLabel, using
// the success tokens instead of warning. This replaces the former line-item rendering of this
// promotion (see EvoRoomPromotions), matching the DP-evolution Figma. When the promotion carries a
// description, an info icon exposes it via a tooltip that opens on hover, keyboard focus AND tap.
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Text, Tooltip } from '@lmn/design-system';
import { useTranslation } from '@/app/components/translations/Translation';
import { Icon } from '@/app/common/icons/Icon';

const SuccessPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.kallisto.space.space50};
  padding: ${({ theme }) => theme.kallisto.space.space50} ${({ theme }) => theme.kallisto.space.space100};
  background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundSuccessSubtle};
  border-radius: ${({ theme }) => theme.kallisto.border.radius.radius50};
  border: 1px solid ${({ theme }) => theme.kallisto.color.border.static.borderSuccessSubtle};
`;

const TooltipWrapper = styled.span`
  display: inline-flex;
  align-items: center;
`;

const InfoButton = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
`;

// Time for the mouse to cross the 9 px gap onto the bubble, a portal child of TooltipWrapper in React's tree.
const HOVER_CLOSE_DELAY_MS = 300;

export function EvoKidsStayFreeBadge({ description }: { description?: string }) {
  const { t } = useTranslation();
  const label = t('vacation.oss.cards.badgets.childrenstayfree');
  const [open, setOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const show = () => {
    setLeaving(false);
    setOpen(true);
  };

  useEffect(() => {
    if (!leaving) return undefined;
    const timer = setTimeout(() => {
      setLeaving(false);
      // Keyboard focus keeps it open until blur (1.4.13 persistent); a mouse click focuses without :focus-visible.
      if (!buttonRef.current?.matches(':focus-visible')) setOpen(false);
    }, HOVER_CLOSE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [leaving]);

  // The DS Tooltip alone is hover/focus-only and never opens on touch, so we drive `open` manually
  // (hover + focus + tap) and dismiss on Escape or an outside mousedown/touchstart.
  useEffect(() => {
    if (!open) return undefined;
    const handleOutside = (event: Event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    // WCAG 1.4.13: Radix's own Escape is a no-op as the DS Tooltip is controlled without onOpenChange,
    // and the bubble can be open while focus is elsewhere (hover, or a click in Safari).
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [open]);

  return (
    <SuccessPill>
      <Text variant="BodySmall" color="textSuccess">
        {label}
      </Text>
      {description && (
        <TooltipWrapper
          ref={wrapperRef}
          // Mouse only: on touch Chromium fires a synthetic mouseleave right after the tap, which would
          // close it. onBlur / outside tap dismiss on touch.
          onPointerEnter={(event) => {
            if (event.pointerType === 'mouse') show();
          }}
          onPointerLeave={(event) => {
            if (event.pointerType === 'mouse') setLeaving(true);
          }}
        >
          <Tooltip content={description} placement="top" open={open}>
            <InfoButton
              ref={buttonRef}
              type="button"
              aria-label={label}
              onClick={show}
              onFocus={show}
              onBlur={() => setOpen(false)}
            >
              <Icon category="Semantics" name="icon-circle-info" size={16} color="iconSuccess" alt="" />
            </InfoButton>
          </Tooltip>
        </TooltipWrapper>
      )}
    </SuccessPill>
  );
}
